import {tokens, colors} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled, {createGlobalStyle} from 'styled-components'

import Header from '@/components/header'
import {PartnerBigform} from '@/components/lead-form/partner'
import SEO from '@/components/seo'
import type {PartnerBigformPageQuery} from '@/graphql-types'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import Image from '@helios/shared/components/image'
import {Main} from '@helios/shared/components/skip-to-content'
import thankYouImage from '@helios/shared/images/thank-you-image.svg'
import {container} from '@helios/shared/styles/grid'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import type {HubspotPartnerChannel} from '@helios/shared/types/marketing-campaigns'
import {
    LifecycleStages,
    LeadType,
    LatestLeadSourceCaptureType,
} from '@helios/shared/types/marketing-campaigns'
import getEnvironmentField from '@helios/shared/utils/get-environment-field'
import mq from '@helios/shared/utils/media-query'

type Props = PageProps<PartnerBigformPageQuery>

const PartnerBigformPage = (props: Props) => {
    const data = props.data.prismicPartnerBigformPage?.data

    if (!data) {
        return handleNoPrismicData()
    }

    const form = getEnvironmentField(data.form_production, data.form_staging)

    return (
        <Wrapper>
            <PageStyles />

            <HeaderWrapper>
                <Header hideLeftNav hideRightNav />
            </HeaderWrapper>

            <Main>
                <MainSection>
                    {data.partner_logo?.url && (
                        <PartnerLogo image={data.partner_logo} width={100} />
                    )}

                    <PartnerBigform
                        content={{
                            title: data.form_title,
                            partner: data.partner,
                            thankYouTitle: data.thank_you_title,
                            thankYouSubtitle: data.thank_you_subtitle,
                            submitButtonText: data.submit_button_text,
                            thankYouImageSrc: thankYouImage,
                            showDescriptionField: data.show_description_field,
                            partnerChannel: {
                                label: data.partner_channel[0].label,
                                preselectedChannel: data.partner_channel[0]
                                    .preselected_channel as HubspotPartnerChannel,
                                accountingPartner: data.partner_channel[0].accounting_partner,
                                affiliatePartner: data.partner_channel[0].affiliate_partner,
                                consultingPartner: data.partner_channel[0].consulting_partner,
                                integrationPartner: data.partner_channel[0].integration_partner,
                                otherReseller: data.partner_channel[0].other_reseller,
                                error: data.partner_channel[0].error,
                                placeholder: data.partner_channel[0].placeholder,
                            },
                        }}
                        hubspotProperties={{
                            lifecyclestage: LifecycleStages.Subscriber,
                            type_of_lead: LeadType.Partner,
                            lead_source_latest_capture_type:
                                LatestLeadSourceCaptureType.PartnerBigForm,
                            generic_email: 'false',
                        }}
                        formId={form.formId}
                    />
                </MainSection>

                {data.social_proof_logos_section?.document && (
                    <IconSectionWrapper>
                        <TitleWithIconGridSection
                            title={data.social_proof_logos_section.document.data.title}
                            items={data.social_proof_logos_section.document.data.logos.map(
                                (item) => ({
                                    icon: item.logo,
                                })
                            )}
                        />
                    </IconSectionWrapper>
                )}
            </Main>
        </Wrapper>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<PartnerBigformPageQuery>) => {
    const pageData = data.prismicPartnerBigformPage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
            preventIndexing={pageData.exclude_robots}
        >
            <link rel="preload" as="image" href={thankYouImage} />
        </SEO>
    )
}

const mobileStylesBreakpoint = 'xs'

const PageStyles = createGlobalStyle`
    body {
        ${mq.min(mobileStylesBreakpoint)} {
            background: ${colors.background.muted};
        }
    }
`

const Wrapper = styled.div`
    padding-bottom: 80px; /* This is needed for making space for the Intercom widget */

    ${mq.min(mobileStylesBreakpoint)} {
        padding-bottom: 0;
    }
`

const HeaderWrapper = styled.div`
    header {
        ${mq.min(mobileStylesBreakpoint)} {
            background: transparent;
        }
    }
`

const MainSection = styled.section`
    ${mq.min(mobileStylesBreakpoint)} {
        ${container}
        &:only-child {
            padding-bottom: ${tokens.spacing40};
        }
    }
`

const PartnerLogo = styled(Image)`
    width: 80px;
    margin: ${tokens.spacing20} auto 0;

    ${mq.min(mobileStylesBreakpoint)} {
        width: 100px;
        margin-bottom: ${tokens.spacing20};
    }
`

const IconSectionWrapper = styled.div`
    ${mq.max(mobileStylesBreakpoint)} {
        display: none;
    }
`

export const query = graphql`
    query PartnerBigformPage($uid: String!, $locale: String!) {
        prismicPartnerBigformPage(uid: {eq: $uid}, lang: {eq: $locale}) {
            _previewable
            data {
                meta_title
                meta_description
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                exclude_robots
                form_title
                form_production {
                    formId
                }
                form_staging {
                    formId
                }
                partner
                partner_logo {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                partner_channel {
                    preselected_channel
                    label
                    accounting_partner
                    affiliate_partner
                    consulting_partner
                    integration_partner
                    other_reseller
                    error
                    placeholder
                }
                thank_you_title
                thank_you_subtitle
                submit_button_text
                show_description_field
                social_proof_logos_section {
                    document {
                        ... on PrismicSocialProofLogosSection {
                            data {
                                title
                                logos {
                                    logo {
                                        alt
                                        copyright
                                        url
                                        dimensions {
                                            height
                                            width
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(PartnerBigformPage)
